/**
 * Die Statuszeile der Glocke: „Alles gelesen. Rückgängig“. (07.10.2026)
 *
 * Martin, 07.10.: *„… ich habe unabsichtlich auch ‚Alles schließen‘ …“* — und er wusste nicht, was passiert war
 * und ob es sich zurücknehmen lässt. Nach dem Abhaken steht darum zehn Sekunden lang eine Zeile am Ende der
 * Karte, die sagt, was geschah, und einen Knopf hat, der es zurücknimmt.
 *
 * Eigenes Modul, weil hier — und nur hier — ein Zeitgeber läuft: EIN Mal, zehn Sekunden, kein Intervall. Die
 * Glocke selbst bleibt ohne jeden Zeitgeber (tests/frontend/glocke-kopfzeile.test.ts: „nicht pulsen“).
 *
 * Die Zeile ist ein `role="status"` und steht leer immer im Dokument: ein Vorleser sagt nur an, was in eine
 * vorhandene Statuszeile geschrieben wird, nicht, was samt ihr neu erscheint.
 */

/** So lange steht „Alles gelesen. Rückgängig“ da. */
export const RUECKGAENGIG_MS = 10_000;

/** Ging „Rückgängig“ schief, bleibt der Knopf mindestens so lange stehen — auch wenn das Fenster fast um ist. */
export const NOCHMAL_MINDEST_MS = 5_000;

export interface Leiste {
  /** Das Element (leer, solange nichts zu sagen ist). */
  element: HTMLElement;
  /** Einen Satz sagen, wenn es sein muss mit einem Knopf; räumt sich nach `ms` selbst auf. */
  sage: (satz: string, knopf?: { wort: string; tut: () => void }, ms?: number) => void;
  /** Den Zeitgeber anhalten (die Karte wird geschlossen). Danach sagt die Leiste nichts mehr und startet keinen Zeitgeber. */
  halte: () => void;
}

/**
 * @param karte  die Karte, die den Fokus bekommt, wenn der Knopf, auf dem er stand, verschwindet
 */
export function baueLeiste(karte: HTMLElement): Leiste {
  const element = document.createElement("div");
  element.className = "glocke-leiste";
  element.setAttribute("role", "status");
  let timer: number | undefined;
  // Die Karte ist zu: eine Antwort, die erst jetzt kommt, darf keinen Zeitgeber an einer abgehängten Leiste starten.
  let abgehaengt = false;

  const raeume = (): void => {
    const fokusDrin = element.contains(document.activeElement);
    element.replaceChildren();
    if (fokusDrin) karte.focus({ preventScroll: true });
  };

  const sage: Leiste["sage"] = (satz, knopf, ms = RUECKGAENGIG_MS) => {
    if (abgehaengt) return;
    window.clearTimeout(timer);
    const hatteFokus = element.contains(document.activeElement);
    const text = document.createElement("span");
    text.className = "glocke-leiste-text";
    text.textContent = satz;
    element.replaceChildren(text);
    if (knopf) {
      const k = document.createElement("button");
      k.type = "button";
      k.className = "glocke-leiste-knopf";
      k.textContent = knopf.wort;
      const tut = knopf.tut;
      // Genau einmal: ein zweiter Tipp findet den Knopf schon gesperrt.
      k.addEventListener("click", () => {
        if (k.disabled) return;
        k.disabled = true;
        tut();
      });
      element.appendChild(k);
      k.focus({ preventScroll: true });
    } else if (hatteFokus) {
      karte.focus({ preventScroll: true });
    }
    timer = window.setTimeout(raeume, ms);
  };

  return {
    element,
    sage,
    halte: () => {
      abgehaengt = true;
      window.clearTimeout(timer);
    },
  };
}
